/**
 * DP1 · NextActionCard
 *
 * Highlighted top-of-page card that answers "o que faço agora?" for the PM.
 * Every PM-facing page mounts one of these; when there's nothing urgent the
 * page omits it (returns null from useNextAction).
 *
 * Design rules:
 *   - Accent dot signals severity (info/warn/err) — no border-left stripe
 *   - Fade-in + translate-up 6px, 220ms, cubic-bezier(0.23, 1, 0.32, 1)
 *     via `animate-next-action-in` Tailwind keyframe
 *   - Primary CTA min-h-[44px] (mobile touch target)
 *   - Full-width on mobile, padding 16px
 *   - No modal — CTA is an onClick, caller decides navigation
 */
import { type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Skeleton } from '@/components/ui/Skeleton';

// ─── Types ────────────────────────────────────────────────────────────────

export type NextActionSeverity = 'info' | 'warn' | 'err';

export interface NextActionCardProps {
  title: string;
  impact?: string;
  primaryAction: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
  };
  secondaryActions?: Array<{ label: string; onClick: () => void }>;
  severity?: NextActionSeverity;
  skeleton?: boolean;
  className?: string;
}

// ─── Severity → token map ────────────────────────────────────────────────

const DOT_CLASSES: Record<NextActionSeverity, string> = {
  info:  'bg-ink-subtle',
  warn:  'bg-warn',
  err:   'bg-err',
};

const BG_CLASSES: Record<NextActionSeverity, string> = {
  info:  'bg-surface-2',
  warn:  'bg-warn/10',
  err:   'bg-err/10',
};

// ─── Component ────────────────────────────────────────────────────────────

export function NextActionCard({
  title,
  impact,
  primaryAction,
  secondaryActions,
  severity = 'info',
  skeleton = false,
  className,
}: NextActionCardProps) {
  if (skeleton) {
    return (
      <div
        aria-hidden
        className={cn(
          'mb-5 w-full rounded-2xl border border-line p-4',
          'bg-surface-2',
          className,
        )}
      >
        <div className="flex items-start gap-3">
          <Skeleton shape="circle" className="mt-1 h-2.5 w-2.5 shrink-0" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton shape="text" className="w-3/4" />
            <Skeleton shape="text" className="w-1/2" />
            <Skeleton className="mt-3 h-11 w-40 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="region"
      aria-label={title}
      className={cn(
        // layout
        'mb-5 w-full rounded-2xl border border-line p-4',
        // severity-tinted background
        BG_CLASSES[severity],
        // entrance animation: fade + lift 6px, 220ms ease-out-strong
        'animate-next-action-in',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        {/* Severity dot — never border-left stripe */}
        <span
          aria-hidden
          className={cn(
            'mt-[5px] inline-block h-2.5 w-2.5 shrink-0 rounded-full',
            DOT_CLASSES[severity],
          )}
        />

        <div className="min-w-0 flex-1">
          {/* Title */}
          <p className="text-[14px] font-semibold leading-snug text-ink">{title}</p>

          {/* Impact — secondary, one line max */}
          {impact && (
            <p className="mt-0.5 text-[13px] leading-snug text-ink-muted">{impact}</p>
          )}

          {/* Actions row */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {/* Primary CTA — min-h-[44px] for mobile touch */}
            <button
              type="button"
              onClick={primaryAction.onClick}
              className={cn(
                'pressable inline-flex min-h-[44px] items-center gap-1.5 rounded-xl',
                'bg-ink px-4 text-[14px] font-medium text-surface shadow-xs',
                'hover:bg-ink/90 active:scale-[0.97]',
                'transition-colors duration-160 ease-out',
              )}
            >
              {primaryAction.icon && (
                <span aria-hidden className="shrink-0">{primaryAction.icon}</span>
              )}
              {primaryAction.label}
            </button>

            {/* Secondary actions — ghost style */}
            {secondaryActions?.map((action) => (
              <button
                key={action.label}
                type="button"
                onClick={action.onClick}
                className={cn(
                  'pressable inline-flex min-h-[44px] items-center rounded-xl px-3',
                  'text-[13px] text-ink-muted hover:text-ink hover:bg-surface-3',
                  'transition-colors duration-160 ease-out',
                )}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
